import type { ElementNature } from './chronoStats';

export type RelationKind = 'precedes' | 'simultaneous';

export interface ScheduleElement {
  id: string;
  resourceId: string;
  sortOrder: number;
  duration: number | null;
  nature: ElementNature;
  description: string;
}

export interface ScheduleRelation {
  id: string;
  fromId: string;
  toId: string;
  kind: RelationKind;
  lag: number;
}

export interface ScheduledBar {
  id: string;
  resourceId: string;
  start: number;
  end: number;
  duration: number;
}

export interface ResourceLoad {
  resourceId: string;
  work: number;
  busy: number;
  idle: number;
  utilization: number | null;
}

export interface ScheduleDiagnostics {
  cycle: number | null;
  byNature: Record<ElementNature, number>;
  resources: ResourceLoad[];
  bottleneckId: string | null;
  elementsAboveTakt: string[];
  unscheduled: string[];
}

export interface ScheduleSuccess {
  ok: true;
  bars: ScheduledBar[];
  diagnostics: ScheduleDiagnostics;
}

export interface ScheduleFailure {
  ok: false;
  error: string;
}

export type ScheduleResult = ScheduleSuccess | ScheduleFailure;

const EPS = 1e-6;

function unionLength(intervals: Array<[number, number]>): number {
  const sorted = intervals
    .filter((interval) => interval[1] > interval[0])
    .sort((left, right) => left[0] - right[0]);
  let length = 0;
  let cursor = Number.NEGATIVE_INFINITY;
  sorted.forEach(([start, end]) => {
    const from = Math.max(start, cursor);
    if (end > from) length += end - from;
    cursor = Math.max(cursor, end);
  });
  return length;
}

export function scheduleCombinedWork(
  elements: ScheduleElement[],
  relations: ScheduleRelation[],
  takt: number | null,
): ScheduleResult {
  const known = new Map(elements.map((element) => [element.id, element]));
  for (const relation of relations) {
    if (relation.fromId === relation.toId) return { ok: false, error: 'Uma relação não pode ligar o elemento a ele mesmo.' };
    if (!known.has(relation.fromId) || !known.has(relation.toId)) {
      return { ok: false, error: 'A relação aponta para elemento de outro cenário.' };
    }
    if (!Number.isFinite(relation.lag)) return { ok: false, error: 'O deslocamento da relação não é um número.' };
  }

  const schedulable = elements.filter((element) => element.duration != null && element.duration >= 0);
  const unscheduled = elements.filter((element) => element.duration == null).map((element) => element.id);
  const activeIds = new Set(schedulable.map((element) => element.id));
  const activeRelations = relations.filter((relation) => activeIds.has(relation.fromId) && activeIds.has(relation.toId));
  for (const relation of relations) {
    const from = known.get(relation.fromId);
    const to = known.get(relation.toId);
    if ((from && from.duration == null) || (to && to.duration == null)) {
      return { ok: false, error: 'A relação usa um elemento sem tempo representativo.' };
    }
  }

  const explicitPair = new Set(activeRelations.flatMap((relation) => {
    const pair = [relation.fromId, relation.toId].sort().join('|');
    return [pair];
  }));

  type Edge = { from: string; to: string; kind: 'lower' | 'equal'; lag: number; explicit: boolean };
  const edges: Edge[] = [];

  const byResource = new Map<string, ScheduleElement[]>();
  schedulable.forEach((element) => {
    const list = byResource.get(element.resourceId) ?? [];
    list.push(element);
    byResource.set(element.resourceId, list);
  });
  byResource.forEach((list) => {
    list.sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id));
    for (let index = 1; index < list.length; index += 1) {
      const previous = list[index - 1];
      const current = list[index];
      const pair = [previous.id, current.id].sort().join('|');
      if (explicitPair.has(pair)) continue;
      edges.push({ from: previous.id, to: current.id, kind: 'lower', lag: 0, explicit: false });
    }
  });

  activeRelations.forEach((relation) => {
    edges.push({
      from: relation.fromId,
      to: relation.toId,
      kind: relation.kind === 'simultaneous' ? 'equal' : 'lower',
      lag: relation.lag,
      explicit: true,
    });
  });

  const outgoing = new Map<string, Edge[]>();
  const indegree = new Map<string, number>();
  schedulable.forEach((element) => indegree.set(element.id, 0));
  edges.forEach((edge) => {
    const list = outgoing.get(edge.from) ?? [];
    list.push(edge);
    outgoing.set(edge.from, list);
    indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
  });

  const queue = schedulable.filter((element) => (indegree.get(element.id) ?? 0) === 0).map((element) => element.id);
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    (outgoing.get(id) ?? []).forEach((edge) => {
      const next = (indegree.get(edge.to) ?? 0) - 1;
      indegree.set(edge.to, next);
      if (next === 0) queue.push(edge.to);
    });
  }
  if (order.length !== schedulable.length) {
    return { ok: false, error: 'As relações formam um ciclo. Nada foi aplicado.' };
  }

  const start = new Map<string, number>();
  const lower = new Map<string, number>();
  const equal = new Map<string, number>();
  schedulable.forEach((element) => lower.set(element.id, Number.NEGATIVE_INFINITY));
  const duration = new Map(schedulable.map((element) => [element.id, element.duration as number]));
  let conflict: string | null = null;

  for (const id of order) {
    const floor = lower.get(id) ?? Number.NEGATIVE_INFINITY;
    const exact = equal.get(id);
    if (exact != null && Number.isFinite(floor) && exact + EPS < floor) {
      conflict = 'A relação pede um início impossível diante das demais dependências.';
      break;
    }
    const value = exact != null ? exact : (Number.isFinite(floor) ? floor : 0);
    start.set(id, value);
    const end = value + (duration.get(id) ?? 0);
    for (const edge of outgoing.get(id) ?? []) {
      const anchor = edge.kind === 'equal' ? value : end;
      const required = anchor + edge.lag;
      if (edge.kind === 'equal') {
        const previous = equal.get(edge.to);
        if (previous != null && Math.abs(previous - required) > EPS) {
          conflict = 'Há duas relações pedindo inícios diferentes para o mesmo elemento.';
          break;
        }
        equal.set(edge.to, required);
      } else {
        lower.set(edge.to, Math.max(lower.get(edge.to) ?? Number.NEGATIVE_INFINITY, required));
      }
    }
    if (conflict) break;
  }
  if (conflict) return { ok: false, error: conflict };

  const rawBars = schedulable.map((element) => {
    const barStart = start.get(element.id) ?? 0;
    const barDuration = duration.get(element.id) ?? 0;
    return { id: element.id, resourceId: element.resourceId, start: barStart, end: barStart + barDuration, duration: barDuration };
  });
  const origin = rawBars.reduce((min, bar) => Math.min(min, bar.start), 0);
  const bars = rawBars.map((bar) => ({ ...bar, start: bar.start - origin, end: bar.end - origin }));
  const cycle = bars.length === 0 ? null : bars.reduce((max, bar) => Math.max(max, bar.end), 0);
  const byNature: Record<ElementNature, number> = { manual: 0, machine: 0, walking: 0, waiting: 0 };
  schedulable.forEach((element) => {
    byNature[element.nature] += element.duration ?? 0;
  });

  const resources: ResourceLoad[] = [...byResource.entries()].map(([resourceId, list]) => {
    const resourceBars = bars.filter((bar) => bar.resourceId === resourceId);
    const work = list.reduce((sum, element) => sum + (element.duration ?? 0), 0);
    const busy = unionLength(resourceBars.map((bar) => [bar.start, bar.end]));
    const idle = cycle == null ? 0 : Math.max(0, cycle - busy);
    return {
      resourceId,
      work,
      busy,
      idle,
      utilization: takt != null && takt > 0 ? busy / takt : null,
    };
  });
  const bottleneckId = resources.reduce<string | null>((best, resource) => {
    if (!best) return resource.resourceId;
    const current = resources.find((item) => item.resourceId === best);
    return current && resource.busy > current.busy ? resource.resourceId : best;
  }, null);

  return {
    ok: true,
    bars,
    diagnostics: {
      cycle,
      byNature,
      resources,
      bottleneckId: bars.length === 0 ? null : bottleneckId,
      elementsAboveTakt: takt == null ? [] : bars.filter((bar) => bar.duration > takt).map((bar) => bar.id),
      unscheduled,
    },
  };
}
